<template>
    <pc-input-group>
        <pc-label
            v-bind:label="label"
            v-bind:for-id="inputId"
            v-bind:show-required="required"
            v-bind:show-help="showHelp"
            v-bind:help-link="showHelpLinkOnly"
            v-on:help-clicked="emitHelpClicked" />
        <div>
            <slot name="help" />
        </div>
        <div class="row gx-3">
            <div v-bind:class="responsiveClasses.designSelector">
                <pc-loader
                    v-bind:loading="auxIsLoading"
                    small>
                    <pc-select
                        v-bind:id="inputId"
                        v-model="selectedIncentiveDesignPublicId"
                        data-test-role="incentive-select-element"
                        v-bind:disabled="readonly"
                        v-bind:show-help="showHelp">
                        <option value="None">
                            {{ selectPrompt }}
                        </option>
                        <option
                            v-for="incentiveDesign in auxIncentiveDesigns"
                            v-bind:key="incentiveDesign.PublicId"
                            v-bind:value="incentiveDesign.PublicId">
                            {{ incentiveDesign.Name }}
                        </option>
                    </pc-select>
                </pc-loader>
            </div>
            <div
                v-bind:class="responsiveClasses.calculateButton"
                class="col d-flex align-items-start">
                <pc-loader
                    v-bind:loading="auxIsCalculating"
                    small>
                    <pc-button
                        v-bind:disabled="!canCalculateIncentive || readonly"
                        data-test-role="incentive-calculate-btn"
                        v-on:click="calculateIncentive">
                        {{ calculateButtonLabel }}
                    </pc-button>
                </pc-loader>
            </div>
        </div>
        <div
            v-if="isAdminOverride"
            v-bind:class="responsiveClasses.adminOverride"
            class="mt-3 container">
            <div class="row gx-3">
                <span class="col-12 fw-bold">{{ adminOverrideControlsLabel }}</span>
            </div>
            <div class="row gx-3">
                <span class="col-12">{{ adminOverrideControlsHelp }}</span>
            </div>
            <div class="row gx-3">
                <div
                    v-bind:class="responsiveClasses.overriddenAmount"
                    class="px-1 d-flex flex-column align-items-start">
                    <pc-label
                        for-id="overrideAmountInput"
                        v-bind:label="overriddenAmountLabel" />
                    <div>
                        <pc-input-group>
                            <pc-input
                                id="overrideAmountInput"
                                v-model="overrideAmountInput"
                                data-test-role="incentive-override-amount"
                                v-bind:validation-enabled="true"
                                v-bind:validator="overrideAmountValidator"
                                v-bind:prepend-items="['$']"
                                v-bind:disabled="readonly"
                                v-bind:validation-group-id="inputId"
                                inputmode="numeric"
                                v-bind:align-text-right="true" />
                        </pc-input-group>
                        <validation-render-helper
                            v-bind:validation-error="validationError"
                            v-bind:validation-group-id="inputId" />
                    </div>
                </div>
                <div
                    v-bind:class="responsiveClasses.overrideReason"
                    class="px-1 d-flex flex-column">
                    <pc-label
                        for-id="overrideReasonInput"
                        v-bind:label="reasonForOverrideLabel" />
                    <div>
                        <pc-input-group>
                            <pc-textarea
                                id="overrideReasonInput"
                                v-model="overrideReason"
                                data-test-role="incentive-override-reason"
                                v-bind:rows="3"
                                v-bind:disabled="readonly" />
                        </pc-input-group>
                    </div>
                </div>
            </div>
        </div>
        <div style="padding-top: 3px" />
        <div class="row gx-3">
            <div v-bind:class="responsiveClasses.incentiveAmount">
                <span class="fw-bold">{{ incentiveAmountLabel }}</span>
                &nbsp;
                <span
                    v-if="incentiveCalculated"
                    data-test-role="incentive-calc-amount">
                    {{ formattedIncentiveAmount }}
                </span>
                <span
                    v-else
                    data-test-role="incentive-calc-amount">
                    {{ notYetCalculatedMessage }}
                </span>
            </div>
            <div
                v-bind:class="responsiveClasses.detailsLink"
                class="col d-flex align-items-start">
                <pc-button
                    link
                    data-test-role="incentive-show-details"
                    v-on:click="showDetails = !showDetails">
                    <span v-if="showDetails">{{ hideCalculationDetailsButtonLabel }}</span>
                    <span v-else>{{ showCalculationDetailsButtonLabel }}</span>
                </pc-button>
            </div>
        </div>
        <pc-transition transition="slide">
            <div
                v-if="showDetails"
                data-test-role="incentive-details"
                class="mx-3 fst-italic">
                <div
                    v-if="displayIncentiveFormulaCalculation"
                    data-test-role="incentive-formula-details">
                    <formula
                        ref="formula"
                        v-bind:formula="evaluatedFormula"
                        v-bind:show-separators="true"
                        simple />
                    <template v-if="formulaIncentiveCappedByApprovedAmountDetail">
                        {{ formulaIncentiveCappedByApprovedAmountDetail }}
                    </template>
                </div>
                <span
                    v-else
                    data-test-role="incentive-calculation-details">
                    <template v-for="(detailLine, index) in incentiveDetailLines">
                        <template v-if="detailLine && detailLine.trim().length > 0">
                            {{ detailLine }}<template v-if="!detailLine.endsWith('.')">.</template>
                            <br v-bind:key="index" />
                        </template>
                    </template>
                </span>
            </div>
        </pc-transition>
        <div
            v-if="outputError"
            data-test-role="incentive-calculation-error"
            class="fw-bold text-danger">
            <span>{{ outputError }}</span>
        </div>
    </pc-input-group>
</template>

<script>
    import base, { validationRenderHelper } from "./_elementBase";
    import formula from "Formula/Components/formula.vue";
    import { VALIDATOR_TYPES, getValidator } from "Validation/validator";
    import { convertToFiniteNumber, isFiniteNumber, htmlUnescape } from "Util/util.js";
    import { FormulaEvaluator } from "Formula/evaluator";
    import { THOUSAND_SEPARATOR_FORMATTER } from "Util/dataFormatters";

    import {
        SUFFIXES,
        NO_INCENTIVE_DESIGN,
        ADMIN_OVERRIDE_DESIGN,
        NOT_YET_CALCULATED,
        NOT_YET_CALCULATED_DETAILS,
        incentiveSelectorController
    } from "Forms/Components/ProjectForm/ProjectElements/incentiveSelectorElementController";

    import { PROJECT_FORM_TYPES } from "Forms/Components/ProjectForm/projectFormStore";
    import { PROJECT_TYPES } from "SharedStores/Project/projectStore";
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/incentiveSelector.i18n";
    import { getResponsiveClassMap } from "Responsive/manager";

    const INCENTIVE_DETAILS_LINE_END_FLAG = ". ";
    const INCENTIVE_DETAILS_ADMIN_START = " [ ";
    const INCENTIVE_DETAILS_ADMIN_END = " ]";

    const responsiveConfig = {
        designSelector: {
            // col-12 col-md-8 col-lg-6
            col: { xs: 12, md: 8, lg: 6 }
        },
        calculateButton: {
            mt: { xs: 2, md: 0 },
            "justify-content": { md: "around" }
        },
        incentiveAmount: {
            // col-12 col-md-8 col-lg-6
            col: { xs: 12, md: 8, lg: 6 }
        },
        detailsLink: {
            "justify-content": { md: "around" }
        },
        adminOverride: {
            // ms-3 ms-sm-5
            ms: { xs: 3, sm: 5 }
        },
        overriddenAmount: {
            // col-12 col-md-4 col-lg-3
            col: { xs: 12, md: 4, lg: 3 }
        },
        overrideReason: {
            // col-12 col-md-6 col-lg-4
            col: { xs: 12, md: 6, lg: 4 }
        }
    };

    export default {
        components: { formula, validationRenderHelper },
        extends: base,
        inject: {
            viewport: {
                from: "viewport",
                default: null
            }
        },
        data() {
            const overrideAmountValidatorOptions = [
                {
                    type: VALIDATOR_TYPES.min,
                    min: 0,
                    acceptThousandSeparated: true,
                    overrideMsg: "Amount must be a value greater than or equal to 0."
                }
            ];
            const overrideAmountValidator = getValidator(this.$app, overrideAmountValidatorOptions);
            return {
                showDetails: false,
                overrideAmountInput: "",
                overrideReason: "",
                overrideAmountValidator: overrideAmountValidator,
                hasBeenCalculated: false,
                evaluatedFormula: null,
                outputError: null
            };
        },
        computed: {
            selectedIncentiveDesignPublicId: {
                get() {
                    if (this.auxStateContains("selectedDesignPublicId")) {
                        if (this.projectElement.auxState.selectedDesignPublicId) {
                            return this.projectElement.auxState.selectedDesignPublicId;
                        }
                    }
                    return NO_INCENTIVE_DESIGN;
                },
                set(value) {
                    if (this.isProjectFormModuleRegistered()) {
                        this.$store.dispatch(PROJECT_FORM_TYPES.actions.changeIncentiveDesign, {
                            elementId: this.element.PublicId,
                            selectedDesignPublicId: value,
                            selectedDesignName: this.getNameForIncentiveWithId(value)
                        });

                        // Clear the Admin Override inputs
                        this.overrideAmountInput = "";
                        this.overrideReason = "";
                    }
                }
            },
            selectedIncentiveDesign() {
                if (this.selectedIncentiveDesignPublicId === NO_INCENTIVE_DESIGN) {
                    return undefined;
                } else {
                    return this.auxIncentiveDesigns.find((d) => d.PublicId === this.selectedIncentiveDesignPublicId);
                }
            },
            displayIncentiveFormulaCalculation() {
                return (
                    this.evaluatedFormula &&
                    (this.userIsAdmin || this.selectedIncentiveDesign.ExposeFormula) &&
                    this.formattedIncentiveAmount != NOT_YET_CALCULATED &&
                    this.hasBeenCalculated
                );
            },
            selectedIncentiveDesignName() {
                return this.getNameForIncentiveWithId(this.selectedIncentiveDesignPublicId);
            },
            savedSelectedIncentiveDesignName() {
                const dataFieldId = this.element.GroupDataFields[SUFFIXES.inputFields.SelectedIncentiveDesign];
                let designName = NO_INCENTIVE_DESIGN;
                if (dataFieldId) {
                    designName = this.$store.getters[PROJECT_TYPES.getters.dataFieldValueFromId](dataFieldId);
                }
                return designName;
            },
            incentiveSelected() {
                return this.selectedIncentiveDesignPublicId !== NO_INCENTIVE_DESIGN;
            },
            isAdminOverride() {
                return this.selectedIncentiveDesignPublicId === ADMIN_OVERRIDE_DESIGN;
            },
            calculateButtonLabel() {
                return this.isAdminOverride ? this.updateButtonLabel : this.calculateButtonLabelText;
            },
            canCalculateIncentive() {
                /* eslint-disable */
                return (
                    // We cannot already be in a "calculating" state
                    !this.auxIsCalculating &&
                    // For Admin Override, the input override amount must be at least 0
                    ((this.isAdminOverride && this.overrideAmount != NOT_YET_CALCULATED && this.overrideAmount >= 0) ||
                        // For all other "normal" calculations, the incentive must be selected
                        (!this.isAdminOverride && this.incentiveSelected))
                );
                /* eslint-enable */
            },
            incentiveCalculated() {
                return this.incentiveDetails !== NOT_YET_CALCULATED_DETAILS;
            },
            incentiveAmount() {
                const dataFieldId = this.element.GroupDataFields[SUFFIXES.outputFields.CalculatedAmount];
                return this.$store.getters[PROJECT_TYPES.getters.dataFieldValueFromId](dataFieldId);
            },
            overrideAmount() {
                const amountNoCurrency = this.overrideAmountInput.replaceAll("$", "");
                const overrideNum = convertToFiniteNumber(THOUSAND_SEPARATOR_FORMATTER.unformat(amountNoCurrency));
                if (isFiniteNumber(overrideNum)) {
                    return overrideNum;
                }
                return NOT_YET_CALCULATED;
            },
            formattedIncentiveAmount() {
                let formattedAmount = NOT_YET_CALCULATED;
                if (this.incentiveCalculated) {
                    const amountNoCurrency = this.incentiveAmount.replaceAll("$", "");
                    const amount = convertToFiniteNumber(THOUSAND_SEPARATOR_FORMATTER.unformat(amountNoCurrency));
                    if (isFiniteNumber(amount)) {
                        // Show $ sign, thousands separators, and .00 cents if the amount is an exact number of dollars
                        this.setOutputError(null);
                        formattedAmount = "$" + THOUSAND_SEPARATOR_FORMATTER.format(amount.toFixed(2));
                    }
                }
                return formattedAmount;
            },
            incentiveAmountLabel() {
                /* eslint-disable */
                return this.isAdminOverride
                    ? this.incentiveSelected
                        ? this.incentiveLabelIncentiveSelected
                        : this.incentiveLabelNoneSelectedText
                    : this.incentiveSelected
                      ? this.calculatedAmountLabelIncentiveSelected
                      : this.calculatedAmountLabelNoneSelectedText;
                /* eslint-enable */
            },
            incentiveDetails() {
                const dataFieldId = this.element.GroupDataFields[SUFFIXES.outputFields.CalculationDetails];
                let details = this.getProjectValue(dataFieldId);
                if (details) {
                    details = details.trim();
                }

                if (details) {
                    if (!this.userIsAdmin) {
                        details = this.removeAdminOnlyDetails(details);
                    }
                } else {
                    if (this.isAdminOverride) {
                        details = this.adminOverrideDefaultDetails;
                    } else {
                        details = NOT_YET_CALCULATED_DETAILS;
                    }
                }
                return details;
            },
            incentiveDetailLines() {
                // In the UI, a period followed by a space (INCENTIVE_DETAILS_LINE_END_FLAG) in the
                //  Incentive Calculation Details is formatted as a period followed by a new line
                //  in order to improve readability.

                // Make sure details ends with period+space, the line-end flag
                let details = this.incentiveDetails.trim();
                if (!details.endsWith(".")) {
                    details += ".";
                }
                details += " ";

                let detailLines = [];
                detailLines = details.split(INCENTIVE_DETAILS_LINE_END_FLAG);

                return detailLines.map(htmlUnescape);
            },
            formulaIncentiveCappedByApprovedAmountDetail() {
                // The formula calculation incentiveDetails will be one line long unless the calculation
                //  was capped by the Approved Incentive Amount.  In that case, that information will be
                //  in the second incentiveDetails line.  When the expanded formula calculation detail
                //  is displayed we want to also show this Approved Amount cap detail.
                let approvedAmountDetail = "";
                if (this.displayIncentiveFormulaCalculation && this.incentiveDetailLines.length > 1) {
                    approvedAmountDetail = this.incentiveDetailLines[1];
                }
                return approvedAmountDetail;
            },
            userIsAdmin() {
                if (this.auxStateContains("userIsAdmin")) {
                    return this.projectElement.auxState.userIsAdmin;
                }
                return false;
            },
            readonly() {
                return this.baseReadonly || this.isPrefill;
            },
            responsiveClasses() {
                return getResponsiveClassMap(this.viewport, responsiveConfig);
            },
            // AuxState
            auxIncentiveDesigns() {
                let designs = [];
                if (this.auxStateContains("incentiveDesigns")) {
                    if (Array.isArray(this.projectElement.auxState.incentiveDesigns)) {
                        designs = this.projectElement.auxState.incentiveDesigns;
                    }
                }
                return designs;
            },
            auxIsLoading() {
                let isLoading = false;
                if (this.auxStateContains("isLoading")) {
                    if (
                        this.projectElement.auxState.isLoading === true ||
                        this.projectElement.auxState.isLoading === false
                    ) {
                        isLoading = this.projectElement.auxState.isLoading;
                    }
                }
                return isLoading;
            },
            auxIsCalculating() {
                let isCalculating = false;
                if (this.auxStateContains("isCalculating")) {
                    if (
                        this.projectElement.auxState.isCalculating === true ||
                        this.projectElement.auxState.isCalculating === false
                    ) {
                        isCalculating = this.projectElement.auxState.isCalculating;
                    }
                }
                return isCalculating;
            },
            ...mapI18n(
                {
                    ...KEYS,
                    adminOverrideControlsHelp() {
                        return {
                            key: KEYS.adminOverrideControlsHelpMessage,
                            values: { buttonLabel: this.calculateButtonLabel }
                        };
                    },
                    incentiveLabelIncentiveSelected() {
                        return {
                            key: KEYS.incentiveLabelNoneSelectedText,
                            values: {
                                name: this.selectedIncentiveDesignName
                            }
                        };
                    },
                    calculatedAmountLabelIncentiveSelected() {
                        return {
                            key: KEYS.incentiveLabelIncentiveSelectedText,
                            values: {
                                name: this.selectedIncentiveDesignName
                            }
                        };
                    }
                },
                NAMESPACE
            )
        },
        watch: {
            auxIsCalculating(newValue) {
                if (newValue) {
                    this.hasBeenCalculated = true;
                } else if (this.hasBeenCalculated) {
                    this.setEvaluatedFormulaIfAny();
                }
            }
        },
        created() {
            if (this.selectedIncentiveDesignPublicId === ADMIN_OVERRIDE_DESIGN) {
                this.setAdminOverrideValues();
            }
        },
        methods: {
            getNameForIncentiveWithId(publicId) {
                const design = this.auxIncentiveDesigns.find(function (d) {
                    return d.PublicId === publicId;
                });
                if (design) {
                    return design.Name;
                }
                return NO_INCENTIVE_DESIGN;
            },
            setOutputError(message) {
                this.outputError = message;
            },
            auxStateContains(propName) {
                if (this.projectElement && this.projectElement.auxState) {
                    return this.projectElement.auxState.hasOwnProperty(propName);
                }
                return false;
            },
            getIncentiveDesignPublicIdByName(designs, designName) {
                const design = designs.find((d) => d.Name === designName);
                if (design) {
                    return design.PublicId;
                }
                return NO_INCENTIVE_DESIGN;
            },
            async clearOutputValues() {
                await incentiveSelectorController.clearOutputValuesAsync(this.element, this.$store.dispatch);
            },
            async calculateIncentive() {
                if (!this.canCalculateIncentive) {
                    this.setOutputError(this.calculateIncentiveErrorMessage);
                    return;
                }

                this.setOutputError(null);

                const error = await this.$store.dispatch(PROJECT_FORM_TYPES.actions.updateIncentive, {
                    elementId: this.element.PublicId,
                    selectedDesignPublicId: this.selectedIncentiveDesignPublicId,
                    overrideAmount: this.overrideAmount,
                    overrideReason: this.overrideReason
                });

                if (this.isAdminOverride) {
                    this.setAdminOverrideValues();
                }

                this.setOutputError(error);
            },
            async setEvaluatedFormulaIfAny() {
                this.evaluatedFormula = null;
                if (this.selectedIncentiveDesign && this.selectedIncentiveDesign.FormulaEvaluator) {
                    const clone = JSON.parse(JSON.stringify(this.selectedIncentiveDesign.FormulaEvaluator));
                    const evaluator = new FormulaEvaluator(
                        this.$app,
                        clone,
                        {
                            rootState: this.$store.state,
                            rootGetters: this.$store.getters
                        },
                        this.$serviceControllers
                    );
                    await evaluator.eval();
                    this.evaluatedFormula = clone;
                }
            },
            setAdminOverrideValues() {
                let amountInput = "";
                const amount = convertToFiniteNumber(THOUSAND_SEPARATOR_FORMATTER.unformat(this.incentiveAmount));
                if (isFiniteNumber(amount)) {
                    amountInput = THOUSAND_SEPARATOR_FORMATTER.format(amount.toFixed(2));
                }
                this.overrideAmountInput = amountInput;
                this.overrideReason = this.incentiveDetails;
            },
            removeAdminOnlyDetails(details) {
                // Note this only removes the first block of text between Admin delimiters, if any;
                //  we're not expecting to have multiple such blocks interspersed through the details
                const bpos = details.indexOf(INCENTIVE_DETAILS_ADMIN_START);
                const epos = details.indexOf(INCENTIVE_DETAILS_ADMIN_END);
                if (-1 < bpos && bpos < epos) {
                    const n = INCENTIVE_DETAILS_ADMIN_END.length;
                    details = details.substring(0, bpos) + details.substring(epos + n);
                }
                return details;
            }
        }
    };
</script>
